import { useAuth0 } from "@auth0/auth0-react";
import { useMutation } from "react-query";
import { ParticipantsService } from "src/services/participants.service";
import { toast } from "react-toastify";
import { ReportEntry } from "src/models/shared/participants";

interface ParticipantExportReportPPTXRequest {
  participantId: string;
  reportEntry: ReportEntry;
}

// Adobe's PDF→PPTX conversion routinely takes longer than the API's 30s request cap,
// so the backend exports asynchronously: PATCH starts the job and returns immediately,
// then this hook polls the status endpoint until the job completes.
const POLL_INTERVAL_MS = 5_000;
const MAX_POLL_ATTEMPTS = 48; // ~4 minutes, matches the Adobe polling-URL validity window

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useParticipantExportReportPPTX = () => {
  const auth0 = useAuth0();
  return useMutation({
    mutationKey: Math.random().toString(), // prevent caching. call every time
    mutationFn: async (request: ParticipantExportReportPPTXRequest) => {
      console.log(
        "useParticipantExportReportPPTX invoked for:",
        request.reportEntry
      );
      const toastId = toast(
        "Exporting report to PPTX.\nThis may take a minute",
        {
          type: "info",
          autoClose: false,
        }
      );
      try {
        const exportResult =
          await ParticipantsService.exportParticipantReportToPPTX(
            request.participantId,
            request.reportEntry,
            auth0
          );
        console.log(`received export result:`, exportResult);

        if (exportResult.status !== "processing") {
          // Job already finished synchronously (e.g. cached result)
          toast.update(toastId, {
            type: "success",
            render: `Report exported to PPTX`,
            autoClose: 4000,
          });
          return exportResult.reportEntry ?? null;
        }

        for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
          await sleep(POLL_INTERVAL_MS);
          const status =
            await ParticipantsService.getParticipantReportPPTXExportStatus(
              request.participantId,
              auth0
            );
          console.log(`ppt export status poll #${attempt + 1}:`, status);

          if (status.status === "done") {
            toast.update(toastId, {
              type: "success",
              render: `Report exported to PPTX`,
              autoClose: 4000,
            });
            return status.reportEntry ?? null;
          }
          if (status.status === "error") {
            toast.update(toastId, {
              type: "error",
              render: `An error occurred exporting the report to PPTX`,
              autoClose: false,
            });
            return null;
          }
          // "processing" → poll again; "none" → job vanished (e.g. completed by
          // another client between polls), refetch and treat as done.
          if (status.status === "none") {
            toast.update(toastId, {
              type: "success",
              render: `Report exported to PPTX`,
              autoClose: 4000,
            });
            return status.reportEntry ?? null;
          }
        }

        toast.update(toastId, {
          type: "error",
          render: `PPTX export timed out. Please try again`,
          autoClose: false,
        });
        return null;
      } catch (err) {
        console.error("PPTX export failed", err);
        toast.update(toastId, {
          type: "error",
          render: `An error occurred exporting the report to PPTX`,
          autoClose: false,
        });
        return null;
      }
    },
  });
};
